import React, {useState} from 'react'
import {Formik, Form} from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

interface Props {
  show: boolean
  handleClose: () => void
  handleOk: () => void
  workOrder: any
  defaultExpectedOnSite: string
  onUpdateExpectedOnSite: (newExpectedOnSite: string, reason: string) => Promise<void>
}

const ExpectedOnSiteModal: React.FC<Props> = ({
  show,
  handleClose,
  handleOk,
  workOrder,
  defaultExpectedOnSite,
  onUpdateExpectedOnSite,
}) => {
  const [expectedOnSiteLoading, setExpectedOnSiteLoading] = useState(false)

  const expectedOnSiteValidationSchema = Yup.object().shape({
    expectedOnSite: Yup.string().required('New Expected On Site is required'),
    expectedOnSiteReason: Yup.string()
      .required('Reason is required')
      .test('trim', 'Reason is required', (value) => value?.trim().length > 0),
  })

  const expectedOnSiteInitialValues = {
    expectedOnSite: defaultExpectedOnSite,
    expectedOnSiteReason: '',
  }

  return (
    <Formik
      initialValues={expectedOnSiteInitialValues}
      validationSchema={expectedOnSiteValidationSchema}
      onSubmit={async (values, {setSubmitting, resetForm}) => {
        setExpectedOnSiteLoading(true)
        await onUpdateExpectedOnSite(values.expectedOnSite, values.expectedOnSiteReason)

        setExpectedOnSiteLoading(false)
        document.getElementById('rd_expectedOnSite_modal_dismiss')!.click()
        handleOk()
        resetForm()
        setSubmitting(false)
      }}
      enableReinitialize
    >
      {({errors, touched, isSubmitting, values, setFieldValue, resetForm}: any) => (
        <>
          <div
            className={`modal fade ${show ? 'show' : ''}`}
            tabIndex={-1}
            id='expectedOnSite_modal'
            style={{display: show ? 'block' : 'none'}}
          >
            <Form>
              <div className='modal-dialog modal-dialog-centered'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>Set Expected On Site</h5>

                    <div
                      className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                      onClick={() => {
                        handleClose()
                        resetForm()
                      }}
                    >
                      <span className='svg-icon svg-icon-2x'></span>
                    </div>
                  </div>

                  <div className='modal-body'>
                    <div className='mb-3'>
                      <label htmlFor='expectedOnSiteFrom' className='form-label'>
                        Current Expected On Site
                      </label>
                      <input
                        type='datetime-local'
                        name='expectedOnSiteFrom'
                        className='form-control form-control-solid'
                        id='expectedOnSiteFrom'
                        value={
                          workOrder.expectedOnSite ? moment(workOrder.expectedOnSite).format('YYYY-MM-DDTHH:mm') : ''
                        }
                        disabled
                      />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='expectedOnSite' className='required form-label'>
                        New Expected On Site
                      </label>
                      <input
                        type='datetime-local'
                        name='expectedOnSite'
                        placeholder=''
                        className={`form-control form-control-solid ${
                          touched.expectedOnSite && errors.expectedOnSite ? 'is-invalid' : ''
                        }`}
                        id='expectedOnSite'
                        value={values.expectedOnSite}
                        onChange={(e) => {
                          setFieldValue('expectedOnSite', e.target.value)
                        }}
                      />
                      {touched.expectedOnSite && errors.expectedOnSite && (
                        <div className='fv-plugins-message-container'>
                          <span className='text-danger' role='alert'>
                            {errors.expectedOnSite}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='expectedOnSiteReason' className='required form-label'>
                        Reason
                      </label>
                      <textarea
                        name='expectedOnSiteReason'
                        className={`form-control form-control-solid ${
                          touched.expectedOnSiteReason && errors.expectedOnSiteReason ? 'is-invalid' : ''
                        }`}
                        id='expectedOnSiteReason'
                        rows={3}
                        value={values.expectedOnSiteReason}
                        onChange={(e) => {
                          setFieldValue('expectedOnSiteReason', e.target.value)
                        }}
                        placeholder='Enter reason for expected on site change...'
                      />
                      {touched.expectedOnSiteReason && errors.expectedOnSiteReason && (
                        <div className='fv-plugins-message-container'>
                          <span className='text-danger' role='alert'>
                            {errors.expectedOnSiteReason}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      onClick={() => {
                        resetForm()
                        handleClose()
                      }}
                      className='btn btn-light'
                      data-bs-dismiss='modal'
                      id='rd_expectedOnSite_modal_dismiss'
                    >
                      Close
                    </button>
                    <button
                      type='submit'
                      id='rd_set_expectedOnSite_submit'
                      className='btn btn-lg btn-primary'
                      disabled={isSubmitting || expectedOnSiteLoading}
                    >
                      {!isSubmitting && !expectedOnSiteLoading && (
                        <span className='indicator-label'>Confirm</span>
                      )}
                      {(isSubmitting || expectedOnSiteLoading) && (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          Please wait...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          </div>
          {show && <div className='modal-backdrop fade show'></div>}
        </>
      )}
    </Formik>
  )
}

export default ExpectedOnSiteModal
